import ChevronLeft from "@mui/icons-material/ChevronLeft";
import LaunchOutlined from "@mui/icons-material/LaunchOutlined";
import Logout from "@mui/icons-material/Logout";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import StarOutlineOutlined from "@mui/icons-material/StarOutlineOutlined";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import type { MenuItemProps } from "@mui/material/MenuItem";
import type { SvgIconTypeMap } from "@mui/material/SvgIcon";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeepsenseLogo from "./DeepsenseLogo";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useAtom } from "jotai";
import { memo, useEffect, useState, useCallback } from "react";
import { parseJWT } from "../utils/auth/isTokenExpired";
import DomainDropdown from "./Authorization/DomainDropdown";
import useAuthorizationManager from "./Authorization/useAuthorizationManager";
import useUserContext from "./Authorization/useUserContext";
import { StyledIconButton } from "./common";
import { AvatarWithFallback } from "./common/AvatarWithFallback";
import { useCallout } from "./Deepchat/Callout";
import { QuestionIsLoadingAtom } from "./Deepchat/state/DeepchatState";
import TopTabsMenu from "./TopTabsMenu";
import useFeatureFlag from "../featureFlags/useFeatureFlag";
import { Feature } from "../featureFlags/featureFlagConfig";

const FEEDBACK_URL = import.meta.env.VITE_APP_FEEDBACK_GSHEET_URL;
const AVATAR_SIZE = "32px";

const StyledMenuItem = ({ children, onClick, sx, disabled }: MenuItemProps) => {
  return (
    <MenuItem disabled={disabled} onClick={onClick} disableRipple sx={{ display: "flex", ...sx }}>
      {children}
    </MenuItem>
  );
};

const StyledIcon = ({ Icon }: { Icon: OverridableComponent<SvgIconTypeMap> }) => {
  return <Icon sx={{ mr: "10px", width: "20px", height: "20px" }} fontSize="medium" />;
};

const StyledMenuWithIcon = ({
  disabled,
  text,
  Icon,
  handleClick,
  sx,
}: {
  disabled: boolean;
  text: string;
  Icon: OverridableComponent<SvgIconTypeMap>;
  handleClick: () => void;
  sx?: SxProps<Theme>;
}) => {
  return (
    <StyledMenuItem disabled={disabled} onClick={handleClick} sx={sx}>
      <StyledIcon Icon={Icon} />
      <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: "400", color: "rgba(0, 0, 0, 0.87)" }}>
        {text}
      </Typography>
    </StyledMenuItem>
  );
};

type TopBarProps = {
  hideSearchBar?: boolean;
  hideLogoutButton?: boolean;
  hideFilterDrawer?: boolean;
  showWelcome?: boolean;
  setHideFilterDrawer?: (show: boolean) => void;
};

const TopBar = ({
  hideSearchBar,
  hideLogoutButton,
  hideFilterDrawer,
  showWelcome = true,
  setHideFilterDrawer,
}: TopBarProps): JSX.Element => {
  const AuthorizationManager = useAuthorizationManager();
  const allowRoleSwitching = useFeatureFlag(Feature.RoleSwitching);
  const user = useUserContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [Callout, closeCallout] = useCallout("See the filters you selected at any time by reopening the filter drawer");
  const [loading] = useAtom(QuestionIsLoadingAtom);

  const handleFeedbackClick = () => {
    // Open the feedback google sheet in a new tab
    window.open(FEEDBACK_URL, "_blank");
  };
  const handleLogout = async () => {
    // close menu
    setAnchorEl(null);
    AuthorizationManager.logout();
  };

  const parsedJWT = parseJWT(user.token);

  const userID = parsedJWT?.userID;
  const userRoles = user.selectedRoles;

  const marginTransition = `margin ${theme.transitions.duration.enteringScreen}ms`;
  const notHideable = undefined;

  useEffect(() => {
    if (loading) {
      closeCallout();
    }
  }, [loading, closeCallout]);

  return (
    <AppBar
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: "none",
        borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
        bgcolor: "white",
      }}
    >
      <Toolbar
        sx={{
          "&": { minHeight: "3.125em", paddingLeft: hideFilterDrawer !== notHideable ? "8px" : "1rem" },
          alignItems: "center",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
        }}
      >
        <Box display="flex" alignItems="center">
          {hideFilterDrawer !== notHideable && (
            <Box>
              <Callout placement={{ top: "100%", left: "50%" }} x="left" y="top" axis="y">
                <IconButton onClick={() => setHideFilterDrawer?.(!hideFilterDrawer)}>
                  {hideFilterDrawer ? <MenuIcon /> : <ChevronLeft />}
                </IconButton>
              </Callout>
            </Box>
          )}
          <DeepsenseLogo />
        </Box>

        <Box>{!hideSearchBar && <TopTabsMenu />}</Box>

        {!hideLogoutButton && (
          <Box justifySelf="end" display="flex" alignItems="center" gap={1.5}>
            {showWelcome && <DomainDropdown />}
            {userID ? (
              <StyledIconButton
                onClick={handleClick}
                sx={{
                  bgcolor: "transparent",
                  borderRadius: "4px",
                  width: "40px",
                  height: "40px",
                }}
              >
                <AvatarWithFallback
                  alt="image"
                  variant="rounded"
                  src={`https://people.roche.com/api/photo/${userID}`}
                  sx={{
                    width: AVATAR_SIZE,
                    height: AVATAR_SIZE,
                  }}
                />
              </StyledIconButton>
            ) : (
              <Tooltip title="Logout" enterTouchDelay={250}>
                <div>
                  <StyledIconButton onClick={handleLogout}>
                    <Logout />
                  </StyledIconButton>
                </div>
              </Tooltip>
            )}
          </Box>
        )}
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{
            "& .MuiPaper-root": { borderRadius: 4 },
          }}
        >
          <StyledMenuItem disabled>
            <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: "400" }}>
              {userID}
            </Typography>
          </StyledMenuItem>
          <Divider />
          {showWelcome && allowRoleSwitching.enabled && (
            <StyledMenuWithIcon
              disabled={false}
              Icon={StarOutlineOutlined}
              handleClick={() => {
                AuthorizationManager.selectRole();
              }}
              text="Back to Welcome"
            />
          )}

          <StyledMenuWithIcon
            disabled={!FEEDBACK_URL}
            Icon={LaunchOutlined}
            handleClick={handleFeedbackClick}
            text="Give Feedback"
          />
          <Divider />
          <StyledMenuWithIcon Icon={LogoutOutlined} handleClick={handleLogout} text="Logout" disabled={!userID} />
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default memo(TopBar);
